/**
 * AppProviders - Optimized context provider composition
 *
 * This component combines related context providers to reduce nesting depth
 * and improve performance. Providers are grouped by their dependencies:
 *
 * 1. Theme & Branding - No dependencies
 * 2. Auth - No dependencies, needed by most others
 * 3. Core App - Depends on Auth (View, Preferences, Menu, Permissions)
 * 4. Feature - Depends on Core (Layout, Drilldown, Tour, Help)
 *
 * Benefits:
 * - Reduced re-render cascading
 * - Cleaner App.tsx
 * - Easier to add new providers
 */
import React, { ReactNode } from 'react';
import { AuthProvider } from '../contexts/AuthContext';
import { ThemeProvider } from '../contexts/ThemeContext';
import { LayoutProvider } from '../contexts/LayoutContext';
import { MenuSettingsProvider } from '../contexts/MenuSettingsContext';
import { UserPermissionsProvider } from '../contexts/UserPermissionsContext';
import { BrandingProvider } from '../contexts/BrandingContext';
import { PreferencesProvider } from '../contexts/PreferencesContext';
import { ViewProvider } from '../contexts/ViewContext';
import { DrilldownProvider } from '../contexts/DrilldownContext';
import { TourProvider } from '../contexts/TourContext';
import { HelpProvider } from '../components/help';
import { ConfirmDialogProvider } from '../contexts/ConfirmDialogContext';
import { PageRefreshProvider } from '../contexts/PageRefreshContext';

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * Theme providers - No dependencies, provide visual configuration
 */
const ThemeProviders: React.FC<AppProvidersProps> = ({ children }) => (
  <ThemeProvider>
    <BrandingProvider>
      {children}
    </BrandingProvider>
  </ThemeProvider>
);

/**
 * Core app providers - Depend on Auth, provide app-wide state
 */
const CoreAppProviders: React.FC<AppProvidersProps> = ({ children }) => (
  <ViewProvider>
    <PreferencesProvider>
      <MenuSettingsProvider>
        <UserPermissionsProvider>
          {children}
        </UserPermissionsProvider>
      </MenuSettingsProvider>
    </PreferencesProvider>
  </ViewProvider>
);

/**
 * Feature providers - Depend on Core, provide feature-specific state
 */
const FeatureProviders: React.FC<AppProvidersProps> = ({ children }) => (
  <LayoutProvider>
    <DrilldownProvider>
      <TourProvider>
        <HelpProvider>
          {children}
        </HelpProvider>
      </TourProvider>
    </DrilldownProvider>
  </LayoutProvider>
);

/**
 * Main AppProviders component
 *
 * Composes all providers in the correct order:
 * Theme -> Auth -> Core -> Feature -> Children
 */
export const AppProviders: React.FC<AppProvidersProps> = ({ children }) => (
  <ThemeProviders>
    <AuthProvider>
      <CoreAppProviders>
        <FeatureProviders>
          <ConfirmDialogProvider>
            <PageRefreshProvider>
              {children}
            </PageRefreshProvider>
          </ConfirmDialogProvider>
        </FeatureProviders>
      </CoreAppProviders>
    </AuthProvider>
  </ThemeProviders>
);

export default AppProviders;
